export const CATEGORY_RECOMMENDATIONS: Record<string, {
  priority: string[];
  detailed: string[];
  resources: string[];
}> = {
  'business-plan': {
    priority: [
      'Develop a comprehensive export business plan with clear objectives and timelines',
      'Establish dedicated export team or assign specific responsibilities to staff members',
      'Create detailed market entry strategies for each target country'
    ],
    detailed: [
      'Conduct thorough market research and feasibility studies for target markets',
      'Define clear export goals, KPIs, and success metrics with measurable outcomes',
      'Allocate adequate budget and resources for export operations including contingency funds',
      'Develop comprehensive risk assessment and mitigation strategies for international markets',
      'Create detailed implementation timeline with milestones, checkpoints, and review periods'
    ],
    resources: [
      'Export Business Plan Template (LSEM)',
      'Market Research Guidelines and Frameworks',
      'Export Feasibility Study Templates',
      'Government Export Development Programs Directory'
    ]
  },
  'finance': {
    priority: [
      'Secure adequate funding for export operations and working capital requirements',
      'Implement foreign exchange risk management strategies and hedging instruments',
      'Explore government export financing programs and trade credit facilities'
    ],
    detailed: [
      'Establish relationships with banks offering comprehensive trade finance services',
      'Master letters of credit, documentary collections, and other payment instruments',
      'Implement currency hedging strategies to manage exchange rate fluctuations effectively',
      'Explore export credit insurance options to protect against non-payment risks',
      'Develop sophisticated pricing strategies that account for all export costs and margins'
    ],
    resources: [
      'Export Finance Calculator and Planning Tools',
      'Comprehensive Trade Finance Guide',
      'Currency Risk Management Software',
      'Export Credit Insurance Providers Directory'
    ]
  },
  'documentation': {
    priority: [
      'Standardize export documentation processes and create comprehensive checklists',
      'Train staff thoroughly on international shipping requirements and customs procedures',
      'Implement digital document management systems for efficiency and compliance'
    ],
    detailed: [
      'Master preparation of commercial invoices, packing lists, and bills of lading',
      'Understand certificate of origin requirements for different target markets',
      'Learn specialized documents requirements (phytosanitary, health certificates)',
      'Implement quality control processes for document accuracy and completeness',
      'Establish strong relationships with customs brokers and freight forwarders'
    ],
    resources: [
      'Comprehensive Export Documentation Checklist',
      'International Customs Procedures Manual',
      'Document Templates Library with Examples',
      'Trade Documentation Software Solutions'
    ]
  },
  'product': {
    priority: [
      'Ensure products meet international standards and obtain necessary certifications',
      'Adapt packaging for international shipping requirements and market preferences',
      'Conduct comprehensive market testing and validation in target countries'
    ],
    detailed: [
      'Research and comply with product standards and regulations in target markets',
      'Obtain necessary certifications (CE marking, ISO standards, FDA approval, etc.)',
      'Adapt product labeling for local language and regulatory compliance requirements',
      'Design robust packaging for long-distance shipping and varying climate conditions',
      'Conduct thorough product testing and quality assurance for international markets'
    ],
    resources: [
      'International Standards Database and Guidelines',
      'Certification Bodies Directory by Country',
      'Export Packaging Guidelines and Best Practices',
      'Product Testing Laboratories Network'
    ]
  },
  'compliance': {
    priority: [
      'Develop comprehensive compliance policies and procedures for international trade',
      'Stay updated on export regulations and requirements in all target markets',
      'Implement robust screening processes for international partners and customers'
    ],
    detailed: [
      'Understand export control regulations and licensing requirements thoroughly',
      'Implement denied party screening procedures and maintain updated databases',
      'Ensure compliance with anti-bribery and corruption laws in all jurisdictions',
      'Maintain proper record-keeping systems for audit and compliance purposes',
      'Stay informed about sanctions, trade restrictions, and regulatory changes'
    ],
    resources: [
      'Export Compliance Manual and Training Materials',
      'Regulatory Updates Service and Alerts',
      'Compliance Training Programs and Certifications',
      'International Trade Legal Advisory Services'
    ]
  },
  'market': {
    priority: [
      'Conduct thorough market research and competitive analysis for target countries',
      'Build strong relationships with local partners, distributors, and industry contacts',
      'Develop market-specific marketing and sales strategies tailored to local preferences'
    ],
    detailed: [
      'Analyze market size, growth potential, and customer segments in detail',
      'Study local competition, pricing strategies, and distribution channels',
      'Understand cultural preferences, business practices, and consumer behavior',
      'Identify and evaluate potential partners, distributors, and key customers',
      'Develop comprehensive market entry strategies and go-to-market plans'
    ],
    resources: [
      'Market Research Reports and Industry Analysis',
      'Trade Missions and International Exhibitions Calendar',
      'Business Matching Services and Platforms',
      'Cultural Training Programs and Resources'
    ]
  },
  'digital': {
    priority: [
      'Optimize website and digital presence for international audiences and markets',
      'Implement e-commerce capabilities and international payment systems',
      'Develop digital marketing strategies tailored to specific target markets'
    ],
    detailed: [
      'Create multilingual and mobile-optimized websites with local content',
      'Implement international SEO and digital marketing strategies effectively',
      'Set up e-commerce platforms with multi-currency support and local payment methods',
      'Utilize social media and digital channels for market penetration and engagement',
      'Implement customer relationship management (CRM) systems for international operations'
    ],
    resources: [
      'Website Internationalization Guide and Tools',
      'E-commerce Platform Comparison and Selection Guide',
      'Digital Marketing Tools and Analytics Platforms',
      'CRM Software Solutions for International Business'
    ]
  },
  'logistics': {
    priority: [
      'Establish relationships with reliable international freight forwarders and logistics providers',
      'Develop efficient international shipping processes and cost management systems',
      'Implement comprehensive shipment tracking systems and insurance coverage'
    ],
    detailed: [
      'Compare and select appropriate shipping methods (air, sea, land) for different markets',
      'Understand Incoterms thoroughly and their implications for cost and risk allocation',
      'Implement inventory management systems for international operations',
      'Develop contingency plans for logistics disruptions and supply chain issues',
      'Optimize packaging and consolidation strategies for cost efficiency and protection'
    ],
    resources: [
      'Freight Forwarder Directory and Evaluation Criteria',
      'Shipping Cost Calculator and Comparison Tools',
      'Comprehensive Incoterms Guide and Examples',
      'Logistics Management Software Solutions'
    ]
  },
  'legal': {
    priority: [
      'Consult with international trade law experts and specialized legal advisors',
      'Protect intellectual property rights in target markets through proper registration',
      'Develop standard international contracts and legal agreements templates'
    ],
    detailed: [
      'Understand international contract law and dispute resolution mechanisms',
      'Register trademarks, patents, and copyrights in all target markets',
      'Develop comprehensive terms and conditions for international sales',
      'Understand product liability and warranty requirements in different jurisdictions',
      'Implement data protection and privacy compliance measures (GDPR, etc.)'
    ],
    resources: [
      'International Trade Lawyers Directory by Jurisdiction',
      'Intellectual Property Registration Services',
      'Contract Templates Library for International Trade',
      'Legal Compliance Guides by Country'
    ]
  },
  'risk': {
    priority: [
      'Implement comprehensive risk assessment and management procedures',
      'Develop crisis management and business continuity plans for international operations',
      'Consider export credit insurance and other risk mitigation tools and strategies'
    ],
    detailed: [
      'Assess political, economic, and commercial risks in all target markets',
      'Implement customer credit assessment and monitoring procedures',
      'Develop foreign exchange risk management strategies and hedging policies',
      'Create detailed contingency plans for various risk scenarios and disruptions',
      'Establish monitoring systems for early risk detection and response'
    ],
    resources: [
      'Risk Assessment Framework and Methodologies',
      'Country Risk Reports and Political Risk Analysis',
      'Insurance Providers Directory and Coverage Options',
      'Crisis Management Templates and Response Plans'
    ]
  }
};

export const getRecommendations = (
  categoryId: string,
  type: 'priority' | 'detailed' | 'resources' = 'priority'
): string[] => {
  const recommendations = CATEGORY_RECOMMENDATIONS[categoryId];

  if (!recommendations) {
    const fallbackRecs = {
      priority: [
        'Seek professional guidance and expert consultation for this category',
        'Attend relevant training programs and industry workshops',
        'Connect with successful exporters and industry mentors for guidance'
      ],
      detailed: [
        'Conduct thorough research on best practices in this area',
        'Develop a systematic approach to improvement with clear milestones',
        'Implement regular monitoring and evaluation processes',
        'Seek specialized training and certification where available',
        'Build relationships with industry experts and service providers'
      ],
      resources: [
        'Industry Association Resources and Guidelines',
        'Professional Training Programs and Certifications',
        'Expert Consultation Services',
        'Best Practice Guides and Case Studies'
      ]
    };
    return fallbackRecs[type];
  }

  return recommendations[type];
};

export const getOverallRecommendations = (weakestCategories: string[]): string[] => {
  const baseRecommendations = [
    'Focus on your three weakest categories as identified in this assessment',
    'Develop a comprehensive 6-12 month improvement plan with specific milestones',
    'Consider engaging with export development professionals for personalized guidance',
    'Join export training programs and industry associations for ongoing support',
    'Start with pilot markets to test your export readiness improvements',
    'Regularly reassess your export readiness as you implement improvements'
  ];

  const categorySpecificRecs: Record<string, string> = {
    'business-plan': 'Prioritize developing a comprehensive export business plan as your foundation',
    'finance': 'Secure adequate funding and establish trade finance relationships early',
    'documentation': 'Master export documentation processes to avoid costly delays',
    'product': 'Ensure product compliance and certification before market entry',
    'compliance': 'Implement robust compliance procedures to avoid legal issues',
    'market': 'Invest in thorough market research before entering new territories',
    'digital': 'Develop strong digital capabilities to compete in modern markets',
    'logistics': 'Establish reliable logistics partnerships for smooth operations',
    'legal': 'Protect your intellectual property and understand legal requirements',
    'risk': 'Implement comprehensive risk management from the start'
  };

  const specificRecs = weakestCategories
    .slice(0, 3)
    .map(categoryId => categorySpecificRecs[categoryId])
    .filter(Boolean);

  return [...baseRecommendations, ...specificRecs];
};

export const getCategoryPriorityLevel = (percentage: number): 'high' | 'medium' | 'low' => {
  if (percentage < 40) return 'high';
  if (percentage < 70) return 'medium';
  return 'low';
};

export const getRecommendationsByPriority = (categoryId: string, priorityLevel: 'high' | 'medium' | 'low'): string[] => {
  const allRecommendations = getRecommendations(categoryId, 'detailed');
  
  switch (priorityLevel) {
    case 'high':
      return allRecommendations.slice(0, 5); // Top 5 recommendations
    case 'medium':
      return allRecommendations.slice(0, 3); // Top 3 recommendations
    case 'low':
      return allRecommendations.slice(0, 2); // Top 2 recommendations
    default:
      return allRecommendations.slice(0, 3);
  }
};